import type { ReactElement } from 'react';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { cn } from '@crm/shared/lib/styles/utils';
import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    Field,
    FieldGroup,
    FieldLabel,
    InputError,
    InputGroup,
    InputGroupInput,
} from '@crm/shared/ui';

import { useLostDealMutation } from './api';
import { REASON_FIELD_NAME, useLostDealDialog, useLostDealForm } from './model';

import type { LostDealFormValues, TLostDealInvalidationScope } from './model';
import type { TDeal } from '@crm/entities/deal';
import type { SubmitHandler } from 'react-hook-form';

interface LostDealProps {
    dealIds: readonly TDeal['id'][];
    invalidationScope: TLostDealInvalidationScope;
    isOpen?: boolean;
    trigger?: ReactElement | null;
    onOpenChange?: (open: boolean) => void;
    onSuccess?: () => void;
}

export const LostDeal = ({
    dealIds,
    invalidationScope,
    isOpen,
    trigger,
    onOpenChange,
    onSuccess,
}: LostDealProps) => {
    const { t } = useTranslation();
    const { lostDealMutationFn, isPending } = useLostDealMutation({
        dealIds,
        invalidationScope,
    });

    const { control, handleSubmit, isSubmitting, resetForm } = useLostDealForm();

    const { handleDialogOpenChange, isDialogOpen } = useLostDealDialog({
        isOpen,
        onOpenChange,
        onClose: resetForm,
    });

    const handleFormSubmit: SubmitHandler<LostDealFormValues> = async (data) => {
        try {
            await lostDealMutationFn(data);
            handleDialogOpenChange(false);
            onSuccess?.();
        } catch {
            // Ошибка уже отображена через mutation.onError.
        }
    };

    const isBatch = dealIds.length > 1;
    const markLostActionLabel = isBatch
        ? t('crm.lostDeal.actions.openBatch')
        : t('crm.common.actions.markLost');

    return (
        <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
            {trigger !== null && (
                <DialogTrigger
                    render={trigger ?? <Button type="button">{markLostActionLabel}</Button>}
                />
            )}
            <DialogContent className="w-135 max-w-135 p-6" closeBtnStyles="top-4 right-4">
                <form className="contents" onSubmit={handleSubmit(handleFormSubmit)}>
                    <DialogHeader>
                        <DialogTitle>{markLostActionLabel}</DialogTitle>
                        <span className="text-xs leading-4 text-smtr-main-tertiary-100">
                            {t('crm.lostDeal.dialog.description')}
                        </span>
                    </DialogHeader>

                    <FieldGroup>
                        <Controller
                            name={REASON_FIELD_NAME}
                            control={control}
                            render={({ field, fieldState }) => (
                                <Field>
                                    <FieldLabel htmlFor={field.name}>
                                        {t('crm.lostDeal.fields.reason.label')}
                                    </FieldLabel>
                                    <InputGroup>
                                        <InputGroupInput
                                            id={field.name}
                                            aria-invalid={fieldState.invalid}
                                            aria-describedby={
                                                fieldState.error ? `${field.name}-error` : undefined
                                            }
                                            className={cn()}
                                            {...field}
                                            onChange={(event) =>
                                                field.onChange(event.currentTarget.value)
                                            }
                                            placeholder={t(
                                                'crm.lostDeal.fields.reason.placeholder'
                                            )}
                                            disabled={field.disabled || isSubmitting || isPending}
                                        />
                                        {fieldState.invalid && (
                                            <InputError
                                                errorMessage={fieldState.error?.message}
                                                iconClassName="size-3.5"
                                            />
                                        )}
                                    </InputGroup>
                                </Field>
                            )}
                        />
                    </FieldGroup>

                    <DialogFooter className="border-t-0 bg-white">
                        <DialogClose
                            render={
                                <Button
                                    disabled={isSubmitting || isPending}
                                    type="button"
                                    variant="outline"
                                >
                                    {t('button.cancel.text')}
                                </Button>
                            }
                        />
                        <Button disabled={isSubmitting || isPending} type="submit">
                            {t('crm.common.actions.markLost')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
